import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import OfferCard from "@/components/offers/OfferCard";
import ApplicationDocuments from "@/components/applications/ApplicationDocuments";
import MessageThread from "@/components/messages/MessageThread";
import api from "@/lib/api";

export default function CandidateApplicationsTab() {
  const { t } = useTranslation();
  const [applications, setApplications] = useState(null);
  const [expandedMessagesId, setExpandedMessagesId] = useState(null);

  useEffect(() => {
    api.get("/applications/candidate/me").then(({ data }) => setApplications(data));
  }, []);

  const setApplicationDocuments = (applicationId, sharedDocuments) => {
    setApplications((prev) => prev.map((a) => (a.id === applicationId ? { ...a, shared_documents: sharedDocuments } : a)));
  };

  if (!applications) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7]" /></div>;

  return (
    <div data-testid="candidate-applications-tab">
      {applications.length === 0 ? (
        <p className="text-center py-16 text-[#2B2B2B]/50" data-testid="candidate-applications-empty-message">{t("search.noResults")}</p>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div key={app.id} className="relative" data-testid={`candidate-application-row-${app.id}`}>
              {app.offer && <OfferCard offer={app.offer} />}
              <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5">
                <Badge className="bg-white border border-black/10 text-[#2B2B2B] rounded-full">
                  {t(`application.status${app.status.charAt(0).toUpperCase() + app.status.slice(1)}`)}
                </Badge>
                {app.mission_closed && (
                  <Badge className="bg-[#34C759]/10 text-[#34C759] border-0 rounded-full gap-1" data-testid={`candidate-application-closed-badge-${app.id}`}>
                    <CheckCircle2 className="h-3 w-3" /> {t("application.missionClosedOn", { date: new Date(app.closed_at).toLocaleDateString() })}
                  </Badge>
                )}
              </div>
              {app.status === "unlocked" && (
                <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 mt-2">
                  <ApplicationDocuments applicationId={app.id} documents={app.shared_documents}
                                        onUploaded={(docs) => setApplicationDocuments(app.id, docs)} />
                  <button onClick={() => setExpandedMessagesId(expandedMessagesId === app.id ? null : app.id)}
                          className="flex items-center gap-1.5 text-sm font-semibold text-[#4ba3b7] hover:underline mt-4"
                          data-testid={`candidate-toggle-messages-button-${app.id}`}>
                    <MessageCircle className="h-4 w-4" /> {t("messages.messagesTitle")}
                    {expandedMessagesId === app.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                  {expandedMessagesId === app.id && (
                    <div className="mt-3">
                      <MessageThread applicationId={app.id} compact />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
